var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Support Ticket Tiles Component
 * Displays customer support tickets in horizontally scrollable tiles.
 * Mirrors the IncidentTiles outage pattern (search, auto-paging, multi-select).
 */
import { Icon, Link, MessageBar, MessageBarType, Spinner, SpinnerSize, Stack, Text, TextField, } from "@fluentui/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { fetchSupportTicketsByTpid, formatSupportTicketCreatedTime, } from "./actions/supportTicketActions";
import { incidentTilesStyles } from "./styles/IncidentTiles.styles";
import { logError } from "../../library/logging/loggingService";
const getSeverityBadgeClass = (severity) => {
    switch (severity.toUpperCase()) {
        case "A":
            return incidentTilesStyles.severityError;
        case "B":
            return incidentTilesStyles.severityWarning;
        case "C":
        default:
            return incidentTilesStyles.severityInformational;
    }
};
const getStateBadgeClass = (state) => {
    switch (state.toLowerCase()) {
        case "active":
            return incidentTilesStyles.statusActive;
        case "resolved":
        case "closed":
            return incidentTilesStyles.statusResolved;
        default:
            return incidentTilesStyles.statusActive;
    }
};
const ITEMS_PER_PAGE = 50;
const SEARCH_DEBOUNCE_MS = 400;
const MAX_PAGE_RETRIES = 2;
const RETRY_BASE_DELAY_MS = 400;
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
// Stable reference so omitting the selection prop does not create a new array
// on every render (which would re-fire the sync effect and loop).
const EMPTY_SELECTION = [];
export const SupportTicketTiles = ({ customerTpid, isVisible, selectedTickets = EMPTY_SELECTION, onSelectionChange, onTicketCountChange, onTicketsLoaded, }) => {
    const [tickets, setTickets] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [isInitialLoading, setIsInitialLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [internalSelectedIds, setInternalSelectedIds] = useState(selectedTickets);
    const [searchQuery, setSearchQuery] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMorePages, setHasMorePages] = useState(false);
    const scrollContainerRef = useRef(null);
    const searchDebounceRef = useRef(null);
    // Monotonically increasing id used to supersede in-flight requests so that a
    // stale response for a previous TPID/search cannot overwrite current state.
    const requestIdRef = useRef(0);
    const ticketsRef = useRef([]);
    // Accumulates every ticket seen for the current customer (keyed by
    // caseNumber) so selected tickets stay resolvable by the parent even when
    // they fall outside the active search results or later pages. Reset when the
    // customer changes.
    const knownTicketsRef = useRef([]);
    const isMountedRef = useRef(true);
    useEffect(() => {
        ticketsRef.current = tickets;
    }, [tickets]);
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);
    const loadTickets = useCallback((page, search, append = false) => __awaiter(void 0, void 0, void 0, function* () {
        if (!customerTpid.trim())
            return;
        // Supersede any in-flight request; only the latest request is allowed to
        // commit results or clear loading state.
        const requestId = ++requestIdRef.current;
        if (append) {
            setIsLoadingMore(true);
        }
        else {
            setIsInitialLoading(true);
            // A non-append load supersedes any in-flight append; clear its loading
            // flag so a superseded append cannot leave load-more/paging stuck.
            setIsLoadingMore(false);
        }
        setError(null);
        const maxAttempts = MAX_PAGE_RETRIES + 1;
        let lastError = null;
        try {
            for (let attempt = 1; attempt <= maxAttempts; attempt++) {
                try {
                    const previouslyLoadedCount = append
                        ? ticketsRef.current.length
                        : 0;
                    const { items, totalCount: count } = yield fetchSupportTicketsByTpid({
                        tpid: customerTpid,
                        page,
                        pageSize: ITEMS_PER_PAGE,
                        search: search || undefined,
                        orderBy: "createdDateTime desc",
                    });
                    if (!isMountedRef.current || requestId !== requestIdRef.current) {
                        return;
                    }
                    const mergedItems = append
                        ? [...ticketsRef.current, ...items]
                        : items;
                    setTickets(mergedItems);
                    setTotalCount(count);
                    setCurrentPage(page);
                    // Treat a zero-item page as terminal: if the API returns no items
                    // while still reporting totalCount > loaded (off-by-one paging,
                    // filtered results, cache staleness, backend bug), never mark more
                    // pages available — otherwise the auto-load effect would request the
                    // next page forever, one HTTP request per commit.
                    setHasMorePages(items.length > 0 && previouslyLoadedCount + items.length < count);
                    if (onTicketCountChange) {
                        onTicketCountChange(count);
                    }
                    // Accumulate every ticket seen for this customer, keyed by
                    // caseNumber, and report the full accumulated set. This keeps
                    // selected tickets resolvable by the parent (title + impacted
                    // product) even when a search or a later page no longer includes
                    // them. The cache is cleared when the customer changes.
                    const knownByCase = new Map(knownTicketsRef.current.map((t) => [t.caseNumber, t]));
                    for (const item of items) {
                        knownByCase.set(item.caseNumber, item);
                    }
                    knownTicketsRef.current = Array.from(knownByCase.values());
                    if (onTicketsLoaded) {
                        onTicketsLoaded(knownTicketsRef.current);
                    }
                    return;
                }
                catch (err) {
                    lastError = err;
                    if (attempt < maxAttempts) {
                        yield delay(RETRY_BASE_DELAY_MS * attempt);
                    }
                }
            }
            if (!isMountedRef.current || requestId !== requestIdRef.current) {
                return;
            }
            logError("Error fetching support tickets", {
                component: "SupportTicketTiles",
                action: "loadTickets",
                error: lastError instanceof Error ? lastError.message : String(lastError),
            });
            setHasMorePages(false);
            setError(append
                ? "Could not load more support tickets. Showing the tickets already loaded."
                : "Failed to load support ticket data");
            if (!append && onTicketCountChange) {
                onTicketCountChange(0);
            }
        }
        finally {
            // Only the latest request may clear the loading state; a superseded
            // request must not flip loading flags for the current request.
            if (requestId === requestIdRef.current && isMountedRef.current) {
                if (append) {
                    setIsLoadingMore(false);
                }
                else {
                    setIsInitialLoading(false);
                }
            }
        }
    }), [customerTpid, onTicketCountChange, onTicketsLoaded]);
    // Initial fetch when component becomes visible or customer TPID changes.
    useEffect(() => {
        if (isVisible && customerTpid.trim()) {
            setCurrentPage(1);
            setSearchQuery("");
            setHasMorePages(false);
            // Reset the previous customer's total so hasLoadedData
            // (totalCount > 0 || tickets.length > 0) is false while the new
            // customer's first page is in flight — otherwise the initial loading
            // spinner is skipped and a stale "no tickets" empty state flashes.
            setTotalCount(0);
            // Cancel any pending debounced search from the previous customer so its
            // stale request cannot supersede this customer's load and commit the
            // wrong customer's tickets.
            if (searchDebounceRef.current) {
                clearTimeout(searchDebounceRef.current);
                searchDebounceRef.current = null;
            }
            // Clear tickets for the previous customer synchronously so neither the
            // display merge nor the accumulated callback cache can carry another
            // customer's tickets forward.
            setTickets([]);
            ticketsRef.current = [];
            knownTicketsRef.current = [];
            loadTickets(1, "", false);
        }
    }, [isVisible, customerTpid]);
    // Debounced search
    const handleSearchChange = (_ev, value) => {
        const newQuery = value !== null && value !== void 0 ? value : "";
        setSearchQuery(newQuery);
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }
        searchDebounceRef.current = setTimeout(() => {
            setCurrentPage(1);
            setHasMorePages(false);
            if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollLeft = 0;
            }
            loadTickets(1, newQuery, false);
        }, SEARCH_DEBOUNCE_MS);
    };
    // Auto-load remaining pages, stopping if a page fails so it is not retried in a loop.
    useEffect(() => {
        if (!isVisible ||
            isInitialLoading ||
            isLoadingMore ||
            !hasMorePages ||
            error) {
            return;
        }
        loadTickets(currentPage + 1, searchQuery, true);
    }, [
        currentPage,
        error,
        hasMorePages,
        isInitialLoading,
        isLoadingMore,
        isVisible,
        loadTickets,
        searchQuery,
    ]);
    // Cleanup debounce on unmount
    useEffect(() => {
        return () => {
            if (searchDebounceRef.current) {
                clearTimeout(searchDebounceRef.current);
            }
        };
    }, []);
    useEffect(() => {
        setInternalSelectedIds(selectedTickets);
    }, [selectedTickets]);
    const handleTicketSelection = (caseNumber) => {
        let newSelectedIds;
        const isCurrentlySelected = internalSelectedIds.includes(caseNumber);
        if (isCurrentlySelected) {
            newSelectedIds = internalSelectedIds.filter((id) => id !== caseNumber);
        }
        else {
            newSelectedIds = [...internalSelectedIds, caseNumber];
        }
        setInternalSelectedIds(newSelectedIds);
        if (onSelectionChange) {
            onSelectionChange(newSelectedIds);
        }
    };
    const hasLoadedData = totalCount > 0 || tickets.length > 0;
    if (!isVisible) {
        return null;
    }
    if (isInitialLoading && !hasLoadedData) {
        return (_jsx("div", Object.assign({ className: incidentTilesStyles.loadingContainer }, { children: _jsxs(Stack, Object.assign({ horizontalAlign: "center", tokens: { childrenGap: 16 } }, { children: [_jsx(Spinner, { size: SpinnerSize.large }), _jsx(Text, { children: "Loading support tickets..." })] })) })));
    }
    if (error && !hasLoadedData) {
        return (_jsx("div", Object.assign({ className: incidentTilesStyles.loadingContainer }, { children: _jsxs(Stack, Object.assign({ horizontalAlign: "center", tokens: { childrenGap: 16 } }, { children: [_jsx(Icon, { iconName: "ErrorBadge", style: { fontSize: "24px", color: "#d13438" } }), _jsx(Text, Object.assign({ style: { color: "#d13438" } }, { children: error }))] })) })));
    }
    if (tickets.length === 0 && !searchQuery.trim() && !isInitialLoading) {
        return (_jsx("div", Object.assign({ className: incidentTilesStyles.loadingContainer }, { children: _jsxs(Stack, Object.assign({ horizontalAlign: "center", tokens: { childrenGap: 16 } }, { children: [_jsx(Icon, { iconName: "CheckMark", style: { fontSize: "24px", color: "#32cd32" } }), _jsx(Text, { children: "No support tickets found for this customer" })] })) })));
    }
    const hasSearchResults = tickets.length > 0;
    return (_jsxs("div", Object.assign({ "data-testid": "support-ticket-tiles" }, { children: [_jsxs("div", Object.assign({ className: incidentTilesStyles.toolbar }, { children: [_jsx(TextField, { className: incidentTilesStyles.searchBox, placeholder: "Search by support request number", value: searchQuery, onChange: handleSearchChange, ariaLabel: "Search support tickets by support request number" }), _jsxs(Text, Object.assign({ className: incidentTilesStyles.pageInfo }, { children: ["Loaded ", tickets.length, " of ", totalCount || tickets.length] }))] })), error && hasLoadedData && (_jsx(MessageBar, Object.assign({ messageBarType: MessageBarType.warning, className: incidentTilesStyles.inlineMessage }, { children: error }))), !hasSearchResults ? (_jsx("div", Object.assign({ className: incidentTilesStyles.loadingContainer }, { children: _jsxs(Stack, Object.assign({ horizontalAlign: "center", tokens: { childrenGap: 12 } }, { children: [_jsx(Icon, { iconName: "SearchIssue", style: { fontSize: "24px", color: "#605e5c" } }), _jsxs(Text, { children: ["No support tickets match \"", searchQuery.trim(), "\"."] })] })) }))) : (_jsx("div", Object.assign({ ref: scrollContainerRef, className: incidentTilesStyles.scrollContainer }, { children: _jsxs("div", Object.assign({ className: incidentTilesStyles.container }, { children: [tickets.map((ticket, index) => {
                            const isSelected = internalSelectedIds.includes(ticket.caseNumber);
                            return (_jsxs("div", Object.assign({ className: `${incidentTilesStyles.tile} ${isSelected ? incidentTilesStyles.tileSelected : ""}`, onClick: () => handleTicketSelection(ticket.caseNumber), role: "checkbox", tabIndex: 0, "aria-checked": isSelected, onKeyDown: (e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        handleTicketSelection(ticket.caseNumber);
                                    }
                                } }, { children: [isSelected && (_jsx("div", Object.assign({ className: incidentTilesStyles.selectedIndicator }, { children: _jsx(Icon, { iconName: "CheckMark", style: { fontSize: "12px", color: "#ffffff" } }) }))), _jsxs("div", Object.assign({ className: incidentTilesStyles.header }, { children: [_jsx("span", Object.assign({ className: incidentTilesStyles.outageBadge }, { children: "SR" })), _jsx("div", Object.assign({ className: incidentTilesStyles.headerContent }, { children: _jsx(Text, Object.assign({ className: incidentTilesStyles.icmLink, title: ticket.caseNumber }, { children: ticket.caseNumber })) }))] })), ticket.title && (_jsx("div", Object.assign({ className: incidentTilesStyles.fieldRow }, { children: _jsx(Text, Object.assign({ variant: "mediumPlus", title: ticket.title, className: incidentTilesStyles.incidentTitle, style: { color: isSelected ? "#0078d4" : "#323130" } }, { children: ticket.title })) }))), _jsxs("div", Object.assign({ className: incidentTilesStyles.fieldRow }, { children: [_jsx("div", Object.assign({ className: incidentTilesStyles.fieldLabel }, { children: "Status" })), _jsx("div", Object.assign({ className: incidentTilesStyles.fieldValue }, { children: _jsx("span", Object.assign({ className: `${incidentTilesStyles.statusBadge} ${getStateBadgeClass(ticket.state)}` }, { children: ticket.state.toUpperCase() })) }))] })), _jsxs("div", Object.assign({ className: incidentTilesStyles.fieldRow }, { children: [_jsx("div", Object.assign({ className: incidentTilesStyles.fieldLabel }, { children: "Severity" })), _jsx("div", Object.assign({ className: incidentTilesStyles.fieldValue }, { children: _jsx("span", Object.assign({ className: `${incidentTilesStyles.severityBadge} ${getSeverityBadgeClass(ticket.severity)}` }, { children: ticket.severity.toUpperCase() })) }))] })), ticket.impactedProduct && (_jsxs("div", Object.assign({ className: incidentTilesStyles.fieldRow }, { children: [_jsx("div", Object.assign({ className: incidentTilesStyles.fieldLabel }, { children: "Product" })), _jsx("div", Object.assign({ className: incidentTilesStyles.fieldValue }, { children: ticket.impactedProduct }))] }))), _jsxs("div", Object.assign({ className: incidentTilesStyles.fieldRow }, { children: [_jsx("div", Object.assign({ className: incidentTilesStyles.fieldLabel }, { children: "Created" })), _jsx("div", Object.assign({ className: incidentTilesStyles.fieldValue }, { children: formatSupportTicketCreatedTime(ticket.createdDateTime) }))] })), ticket.linkedIcmIds.length > 0 && (_jsxs("div", Object.assign({ className: incidentTilesStyles.fieldRow }, { children: [_jsx("div", Object.assign({ className: incidentTilesStyles.fieldLabel }, { children: "Linked ICMs" })), _jsxs("div", Object.assign({ className: incidentTilesStyles.arrayValue }, { children: [_jsx("div", Object.assign({ className: incidentTilesStyles.arrayValueItem }, { children: ticket.linkedIcmIds[0] })), ticket.linkedIcmIds.length > 1 && (_jsxs(Link, Object.assign({ as: "span", style: { fontSize: "12px", whiteSpace: "nowrap" }, title: ticket.linkedIcmIds.join(", ") }, { children: ["+", ticket.linkedIcmIds.length - 1, " more"] })))] }))] })))] }), `${ticket.caseNumber}-${index}`));
                        }), isLoadingMore && (_jsx("div", Object.assign({ className: incidentTilesStyles.loadingMoreTile }, { children: _jsx(Spinner, { size: SpinnerSize.medium, label: "Loading more..." }) })))] })) })))] })));
};
export default SupportTicketTiles;
